import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Facebook, Instagram, Twitter } from "lucide-react";
import { LOGO_URL, subscribeNewsletter } from "../lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const res = await subscribeNewsletter({ email });
      toast.success(res.message);
      setEmail("");
    } catch {
      toast.error("Please enter a valid email address.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="mt-20 bg-[var(--navy)] text-white/80">
      <div className="border-b border-white/10">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <img src={LOGO_URL} alt="News-Leader" className="mb-4 h-14 rounded bg-white/95 p-2 object-contain" />
            <p className="font-serif-body text-sm leading-relaxed">
              Florida's Oldest Weekly Newspaper. Serving Amelia Island, Fernandina Beach, Yulee and Nassau County since 1854.
            </p>
            <div className="mt-4 flex gap-3">
              <SocialIcon Icon={Facebook} />
              <SocialIcon Icon={Instagram} />
              <SocialIcon Icon={Twitter} />
            </div>
          </div>

          <div>
            <h4 className="eyebrow mb-4 text-[var(--gold)]">Sections</h4>
            <ul className="font-ui space-y-2 text-sm">
              <FooterLink to="/section/news">Local News</FooterLink>
              <FooterLink to="/section/sports">High School Sports</FooterLink>
              <FooterLink to="/section/entertainment">Entertainment</FooterLink>
              <FooterLink to="/section/columnists">Columnists</FooterLink>
              <FooterLink to="/galleries">Photo Galleries</FooterLink>
              <FooterLink to="/marketplace">Marketplace</FooterLink>
              <FooterLink to="/contests">Contests & Giveaways</FooterLink>
              <FooterLink to="/directory">Business Directory</FooterLink>
            </ul>
          </div>

          <div>
            <h4 className="eyebrow mb-4 text-[var(--gold)]">Advertise & Connect</h4>
            <ul className="font-ui space-y-2 text-sm">
              <FooterLink to="/media-kit">Media Kit & Ad Rates</FooterLink>
              <FooterLink to="/directory">List Your Business</FooterLink>
              <FooterLink to="/subscribe">Subscribe</FooterLink>
              <FooterLink to="/about">About the Newsroom</FooterLink>
            </ul>
            <div className="font-serif-body mt-4 space-y-1.5 text-sm">
              <p className="flex items-center gap-2"><MapPin size={14} className="text-[var(--gold)]" /> 511 Ash St, Fernandina Beach, FL</p>
              <p className="flex items-center gap-2"><Phone size={14} className="text-[var(--gold)]" /> (904) 261-3696</p>
              <p className="flex items-center gap-2"><Mail size={14} className="text-[var(--gold)]" /> news@fbnewsleader.com</p>
            </div>
          </div>

          <div>
            <h4 className="eyebrow mb-4 text-[var(--gold)]">Local News Alerts</h4>
            <p className="font-serif-body mb-3 text-sm">Get the day's top island headlines in your inbox.</p>
            <form onSubmit={submit} className="space-y-2" data-testid="footer-newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="newsletter-email-input"
                className="font-serif-body w-full rounded-md border border-white/20 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-[var(--gold)]"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="newsletter-submit"
                className="font-ui w-full rounded-md bg-[var(--gold)] px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white transition-colors hover:bg-[#b08f48] disabled:opacity-60"
              >
                {busy ? "Signing up…" : "Sign Up Free"}
              </button>
            </form>
          </div>
        </div>
      </div>
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 px-4 py-5 text-xs text-white/50 sm:flex-row">
        <p className="font-ui">© {new Date().getFullYear()} The News-Leader. All rights reserved.</p>
        <p className="font-ui flex items-center gap-3">
          <Link to="/admin" className="link-underline hover:text-white" data-testid="footer-cms-link">Staff CMS</Link>
          <span>Privacy Policy · Accessibility · Do Not Sell My Info</span>
        </p>
      </div>
    </footer>
  );
}

const SocialIcon = ({ Icon }) => (
  <a href="https://www.facebook.com/" target="_blank" rel="noreferrer" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 transition-colors hover:border-[var(--gold)] hover:text-[var(--gold)]">
    <Icon size={16} />
  </a>
);

const FooterLink = ({ to, children }) => (
  <li>
    <Link to={to} className="link-underline hover:text-white">{children}</Link>
  </li>
);
